import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { apiWhopCheckout, apiRecordConsent, apiValidateAffiliate, apiSessionSummary, getSessionId } from "@/lib/api";
import { getReferralCode } from "@/lib/referral";
import { useAuth } from "@/lib/AuthContext";
import { Check } from "lucide-react";

const PLANS = [
  {
    tier: "thrifter",
    name: "Thrifter",
    price: "$5.99",
    period: "/month",
    scans: "15 scans every month",
    description: "For weekend thrifters flipping the occasional find.",
    popular: false,
  },
  {
    tier: "reseller",
    name: "Reseller",
    price: "$14.99",
    period: "/month",
    scans: "60 scans every month",
    description: "For active resellers flipping sneakers, streetwear and electronics.",
    popular: true,
  },
  {
    tier: "pro",
    name: "Pro",
    price: "$29.99",
    period: "/month",
    scans: "125 scans + priority queue",
    description: "For high-volume flippers running a real store — fast scans, deep comps.",
    popular: false,
  },
];

const RANK = { free: 0, thrifter: 1, reseller: 2, pro: 3 };

export default function UpgradeModal({ open, onClose, currentPlan = "free", status = "inactive" }) {
  const { user } = useAuth();
  const [busy, setBusy] = useState(null);
  const [agreed, setAgreed] = useState(false);
  const [email, setEmail] = useState("");
  // Affiliate ("creator code") — pre-filled from first-touch cookie, editable.
  const [affCode, setAffCode] = useState("");
  const [affStatus, setAffStatus] = useState("idle"); // idle | checking | valid | invalid
  const [affName, setAffName] = useState("");
  const [summary, setSummary] = useState(null); // { items_priced, resale_value_usd }

  // Pull concrete "what you got out of it" numbers so the paywall isn't abstract.
  useEffect(() => {
    if (!open) return;
    apiSessionSummary(getSessionId())
      .then((r) => {
        if ((r?.items_priced ?? 0) > 0) setSummary(r);
      })
      .catch(() => { /* silent — the paywall still works without it */ });
  }, [open]);

  // Prefill the field from the 90-day cookie when the modal opens, and
  // eagerly validate so users see the creator's name confirming attribution.
  useEffect(() => {
    if (!open) return;
    const c = getReferralCode();
    if (c && !affCode) {
      setAffCode(c);
      setAffStatus("checking");
      apiValidateAffiliate(c)
        .then((r) => {
          if (r?.valid) { setAffStatus("valid"); setAffName(r.name || ""); }
          else { setAffStatus("invalid"); setAffName(""); }
        })
        .catch(() => setAffStatus("invalid"));
    }
  }, [open]);  // eslint-disable-line react-hooks/exhaustive-deps

  const validateAff = async () => {
    const c = (affCode || "").trim().toUpperCase();
    if (!c) { setAffStatus("idle"); setAffName(""); return; }
    setAffStatus("checking");
    try {
      const r = await apiValidateAffiliate(c);
      if (r?.valid) { setAffStatus("valid"); setAffName(r.name || ""); }
      else { setAffStatus("invalid"); setAffName(""); }
    } catch {
      setAffStatus("invalid"); setAffName("");
    }
  };

  const buy = async (plan) => {
    if (!agreed) {
      toast.error("Please agree to the Terms, Privacy Policy and Refund Policy first");
      return;
    }
    // Anon users must supply an email so we can match their payment back to
    // them even if they never sign in. Auth'd users skip this — server auto-
    // fills from user.email.
    const rawEmail = (email || "").trim();
    if (!user && !rawEmail) {
      toast.error("Enter your email so we can find your credits if anything goes wrong");
      return;
    }
    if (rawEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(rawEmail)) {
      toast.error("That email looks off — please double-check");
      return;
    }
    // Only pass a code we've confirmed is valid — Whop will 422 on unknown
    // codes and drop the whole checkout, which is worse than no attribution.
    const codeToSend = affStatus === "valid" ? (affCode || "").trim().toUpperCase() : null;
    setBusy(plan.tier);
    try {
      try {
        await apiRecordConsent(getSessionId());
      } catch (e) {
        toast.error("Couldn't record consent — please retry");
        setBusy(null);
        return;
      }
      const res = await apiWhopCheckout(plan.tier, getSessionId(), rawEmail || null, codeToSend);
      if (res?.checkout_url) {
        window.location.href = res.checkout_url;
      } else {
        toast.error("Checkout unavailable");
        setBusy(null);
      }
    } catch (e) {
      const msg = e?.response?.data?.detail;
      if (msg === "policy_consent_required") {
        toast.error("Please agree to the policies first — your consent expired");
      } else if (typeof msg === "string" && msg.startsWith("whop_plan_missing:")) {
        toast.error(`This plan is temporarily unavailable — try another tier or contact support`);
      } else {
        toast.error(msg || "Checkout failed");
      }
      setBusy(null);
    }
  };

  const buttonLabelFor = (planTier) => {
    if (RANK[planTier] === RANK[currentPlan]) return { text: "Current Plan", disabled: true, action: "current" };
    if (RANK[planTier] > RANK[currentPlan]) return { text: status === "active" ? "Upgrade Plan" : "Pay with Whop", disabled: false, action: "upgrade" };
    return { text: "Downgrade", disabled: false, action: "downgrade" };
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="upgrade-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-4"
          style={{ background: "rgba(0,0,0,0.8)", backdropFilter: "blur(10px)" }}
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.95, y: 24, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.95, y: 24, opacity: 0 }}
            transition={{ type: "spring", damping: 22, stiffness: 260 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-4xl bg-white border border-[#E9ECEF] rounded-3xl p-6 md:p-8 shadow-2xl max-h-[92vh] overflow-y-auto hide-scrollbar"
          >
            <button
              data-testid="upgrade-modal-close"
              onClick={onClose}
              className="absolute top-4 right-4 text-[#64748B] hover:text-[#0F172A] transition-colors text-sm w-7 h-7 rounded-full flex items-center justify-center hover:bg-[#F1F5F9]"
            >
              ✕
            </button>

            <div className="text-center mb-6 sm:mb-8">
              <span className="inline-block px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/40 text-[#047857] text-[11px] font-semibold tracking-wide uppercase mb-3">
                Monthly subscriptions
              </span>
              <h2 className="font-display text-2xl md:text-3xl font-bold text-[#0F172A] mb-2">Choose your monthly plan</h2>
              <p className="text-sm text-[#475569] max-w-md mx-auto leading-relaxed">
                Get automated monthly scan resets, instant AI item authentication, and listing exports. Cancel or change your subscription anytime.
              </p>
            </div>

            {/* Concrete recap of what this session has already priced. Only
                shows when there's actual data — never fabricated. */}
            {summary && (
              <div
                data-testid="paywall-session-summary"
                className="mb-5 mx-auto max-w-md rounded-2xl bg-[#F0FDF4] border border-[#86EFAC] px-4 py-3 text-center"
              >
                <p className="text-[13px] text-[#166534] leading-relaxed">
                  You&apos;ve priced{" "}
                  <strong className="font-mono-num text-[#052E16]">{summary.items_priced}</strong>{" "}
                  {summary.items_priced === 1 ? "item" : "items"} worth{" "}
                  <strong className="font-mono-num text-[#052E16]">
                    ${summary.resale_value_usd.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </strong>{" "}
                  in resale value.
                </p>
              </div>
            )}

            {/* Pre-purchase checklist — sign-in requirement + timing */}
            <div className="mb-6 rounded-2xl bg-[#FFFBEB] border border-[#F59E0B] p-4">
              <div className="flex items-start gap-3">
                <div className="w-5 h-5 rounded-full bg-[#F59E0B]/25 border border-[#B45309] flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-[#78350F] text-[11px] font-bold">!</span>
                </div>
                <div className="text-[13px] text-[#78350F] leading-relaxed">
                  <p className="font-semibold text-[#78350F] mb-1" data-testid="prepurchase-title">Before you buy — read this:</p>
                  <ul className="space-y-1 text-[#78350F] list-disc list-inside marker:text-[#B45309]">
                    <li data-testid="prepurchase-signin"><span className="font-semibold">Sign in with Google first.</span> Anonymous purchases may not validate against your account and credits could get stuck.</li>
                    <li data-testid="prepurchase-delay"><span className="font-semibold">Credits take up to 30 seconds</span> to appear after checkout. If you don&apos;t see them, hit the <span className="font-mono-num">Sync Credits</span> button in the header.</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {PLANS.map((plan, i) => {
                const label = buttonLabelFor(plan.tier);
                const isCurrent = label.action === "current";
                return (
                  <motion.div
                    key={plan.tier}
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.05 * i }}
                    whileHover={{ y: isCurrent ? 0 : -4, transition: { duration: 0.2 } }}
                    className={`relative flex flex-col justify-between p-6 rounded-2xl border transition-colors ${
                      isCurrent
                        ? "bg-emerald-500/[0.08] border-emerald-500/50 shadow-lg shadow-emerald-500/10"
                        : plan.popular
                          ? "bg-white/[0.06] border-emerald-500/50 shadow-lg shadow-emerald-500/10"
                          : "bg-white/[0.02] border-white/10 hover:border-white/20"
                    }`}
                    data-testid={`plan-card-${plan.tier}`}
                  >
                    {isCurrent && (
                      <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-emerald-500 text-black text-[10px] font-bold uppercase tracking-wider px-3 py-0.5 rounded-full flex items-center gap-1">
                        <Check size={10} strokeWidth={3} /> Current Plan
                      </span>
                    )}
                    {!isCurrent && plan.popular && (
                      <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-emerald-500 text-black text-[10px] font-bold uppercase tracking-wider px-3 py-0.5 rounded-full">
                        Most Popular
                      </span>
                    )}
                    <div>
                      <h3 className="text-sm font-semibold text-[#0F172A]">{plan.name}</h3>
                      <div className="my-3">
                        <span className="text-3xl font-extrabold text-[#0F172A]">{plan.price}</span>
                        <span className="text-xs text-[#475569]">{plan.period}</span>
                      </div>
                      <p className="text-xs font-mono-num text-[#047857] font-semibold mb-2">{plan.scans}</p>
                      <p className="text-[13px] text-[#475569] leading-relaxed mb-6">{plan.description}</p>
                    </div>
                    <button
                      data-testid={`plan-buy-${plan.tier}`}
                      disabled={busy === plan.tier || label.disabled}
                      onClick={() => !label.disabled && buy(plan)}
                      style={label.disabled ? undefined : (plan.popular ? { color: "#0F172A" } : { color: "#FFFFFF" })}
                      className={`w-full py-3 rounded-full text-sm font-semibold text-center transition-colors flex items-center justify-center gap-1.5 min-h-[44px] ${
                        label.disabled
                          ? "bg-[#E2E8F0] border border-[#CBD5E1] cursor-not-allowed"
                          : plan.popular
                          ? "bg-white hover:bg-zinc-100 shadow-md border border-[#0F172A]"
                          : "bg-[#0F172A] hover:bg-[#1E293B]"
                      }`}
                    >
                      <span style={label.disabled ? { color: "#475569" } : undefined}>{busy === plan.tier ? "Redirecting…" : label.text}</span>
                    </button>
                  </motion.div>
                );
              })}
            </div>

            {!["thrifter", "reseller", "pro"].includes(currentPlan) && (
              <div className="mt-6 p-4 rounded-2xl bg-[#F0FDF4] border border-[#BBF7D0]" data-testid="starter-row">
                <p className="text-[13px] font-semibold text-[#0F172A]">Not ready for a plan?</p>
                <div className="mt-3">
                  <button
                    data-testid="starter-6-btn"
                    onClick={() => buy({ tier: "starter6" })}
                    disabled={busy === "starter6"}
                    className="w-full px-4 py-2.5 rounded-full bg-white border border-[#0F172A]/15 text-[#0F172A] text-sm font-semibold hover:border-[#0F172A]/40 transition-colors disabled:opacity-40"
                  >
                    {busy === "starter6" ? "Opening checkout…" : "⚡ 6 credits — $2.99 · one-time, no subscription"}
                  </button>
                </div>
                <p className="mt-2 text-[11px] text-[#64748B]">One per account. Plans give you more per credit.</p>
              </div>
            )}
            {["thrifter", "reseller", "pro"].includes(currentPlan) && (
              <div className="mt-6 p-4 rounded-2xl bg-[#F0FDF4] border border-[#BBF7D0]" data-testid="topup-row">
                <p className="text-[13px] font-semibold text-[#0F172A]">Ran out mid-month? Refill instantly.</p>
                <div className="mt-3 flex flex-col sm:flex-row gap-2.5">
                  <button
                    data-testid="topup-10-btn"
                    onClick={() => buy({ tier: "topup10" })}
                    disabled={busy === "topup10"}
                    className="flex-1 px-4 py-2.5 rounded-full bg-white border border-[#0F172A]/15 text-[#0F172A] text-sm font-semibold hover:border-[#0F172A]/40 transition-colors disabled:opacity-40"
                  >
                    {busy === "topup10" ? "Opening checkout…" : "+10 credits — $4.19"}
                  </button>
                  <button
                    data-testid="topup-20-btn"
                    onClick={() => buy({ tier: "topup20" })}
                    disabled={busy === "topup20"}
                    className="flex-1 px-4 py-2.5 rounded-full bg-[#0F172A] text-white text-sm font-semibold hover:bg-[#1E293B] transition-colors disabled:opacity-40"
                  >
                    {busy === "topup20" ? "Opening checkout…" : "+20 credits — $7.99 · best value"}
                  </button>
                </div>
                <p className="mt-2.5 text-[11px] text-[#64748B]">
                  Top-up credits roll over while you’re subscribed. Plans stay the better per-credit deal.
                </p>
              </div>
            )}

            <div className="mt-6 p-4 rounded-2xl bg-[#F8F9FA] border border-[#E9ECEF]">
              <p className="text-[13px] text-[#475569] leading-relaxed">
                <span className="text-[#0F172A] font-semibold">Manage or cancel anytime.</span> Log into your{" "}
                <a
                  href="https://whop.com/orders"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#047857] underline decoration-dotted hover:decoration-solid"
                >
                  Whop Dashboard
                </a>{" "}
                using the exact same email address you used during purchase. Subscriptions renew monthly and cancel in one click.
              </p>
            </div>

            {!user && (
              <div className="mt-4">
                <label className="block text-xs font-semibold text-[#0F172A] mb-1.5" htmlFor="checkout-email-input">
                  Your email
                </label>
                <input
                  id="checkout-email-input"
                  data-testid="checkout-email-input"
                  type="email"
                  autoComplete="email"
                  inputMode="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-3.5 py-2.5 rounded-lg border border-[#E9ECEF] bg-white text-sm text-[#0F172A] placeholder:text-[#94A3B8] focus:outline-none focus:border-[#0F172A] focus:ring-2 focus:ring-[#0F172A]/10"
                  style={{ minHeight: "44px" }}
                />
                <p className="text-[11px] text-[#64748B] mt-1.5 leading-relaxed">
                  Use the same email at Whop checkout. This is how we find your credits if the tab crashes or you check out on another device.
                </p>
              </div>
            )}

            {/* Creator code (optional). Attribution + double first-month scans. */}
            <div className="mt-4">
              <label className="block text-xs font-semibold text-[#0F172A] mb-1.5" htmlFor="checkout-affcode-input">
                Creator code <span className="text-[#94A3B8] font-normal">(optional — get double scans your first month)</span>
              </label>
              <div className="relative">
                <input
                  id="checkout-affcode-input"
                  data-testid="checkout-affcode-input"
                  type="text"
                  autoComplete="off"
                  spellCheck={false}
                  placeholder="THRIFTKING"
                  value={affCode}
                  onChange={(e) => {
                    setAffCode(e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, "").slice(0, 40));
                    if (affStatus !== "idle") setAffStatus("idle");
                    setAffName("");
                  }}
                  onBlur={validateAff}
                  className={`w-full px-3.5 py-2.5 pr-24 rounded-lg border bg-white text-sm text-[#0F172A] placeholder:text-[#94A3B8] focus:outline-none focus:ring-2 uppercase tracking-wider ${
                    affStatus === "valid"
                      ? "border-[#047857] focus:border-[#047857] focus:ring-[#047857]/10"
                      : affStatus === "invalid"
                        ? "border-[#B91C1C] focus:border-[#B91C1C] focus:ring-[#B91C1C]/10"
                        : "border-[#E9ECEF] focus:border-[#0F172A] focus:ring-[#0F172A]/10"
                  }`}
                  style={{ minHeight: "44px" }}
                />
                <div className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] font-semibold pointer-events-none">
                  {affStatus === "checking" && <span className="text-[#64748B]">Checking…</span>}
                  {affStatus === "valid" && (
                    <span className="text-[#047857] flex items-center gap-1" data-testid="affcode-status-valid">
                      <Check size={12} strokeWidth={3} /> Applied
                    </span>
                  )}
                  {affStatus === "invalid" && <span className="text-[#B91C1C]" data-testid="affcode-status-invalid">Not found</span>}
                </div>
              </div>
              <p className="text-[11px] text-[#64748B] mt-1.5 leading-relaxed" data-testid="affcode-hint">
                {affStatus === "valid" && affName
                  ? <>Code applied — supporting <span className="font-semibold text-[#0F172A]">{affName}</span>. You&apos;ll get <span className="font-semibold text-[#0F172A]">2× scans</span> your first month.</>
                  : affStatus === "invalid"
                    ? "We couldn't find that code — you can still check out, but no bonus scans will apply."
                    : "Have a creator code? Enter it here to unlock 2× scans your first month."}
              </p>
            </div>

            <label className="mt-5 flex items-start gap-2.5 text-[11px] sm:text-xs text-[#475569] leading-relaxed cursor-pointer select-none" data-testid="checkout-agree-label">
              <input
                type="checkbox"
                data-testid="checkout-agree-checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                className="mt-0.5 w-4 h-4 rounded border-[#CBD5E1] bg-white text-[#047857] focus:ring-[#047857]/40 flex-shrink-0"
              />
              <span>
                I agree to the{" "}
                <Link to="/terms" className="text-[#047857] font-semibold underline decoration-solid hover:decoration-2" target="_blank">Terms</Link>,{" "}
                <Link to="/privacy" className="text-[#047857] font-semibold underline decoration-solid hover:decoration-2" target="_blank">Privacy Policy</Link>{" "}
                and{" "}
                <Link to="/refund-policy" className="text-[#047857] font-semibold underline decoration-solid hover:decoration-2" target="_blank">Refund Policy</Link>
                {" "}— <strong className="text-[#0F172A]">all sales are final, no refunds</strong>.
              </span>
            </label>

            <p className="text-center text-[10px] text-zinc-500 mt-4">
              Secure checkout powered by Whop. Auto-renews monthly, cancel anytime in 1 click.
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
